import React, { useEffect, useMemo } from 'react';
import {
  StackScreen,
  Flex,
  useScreenSize,
  SafeAreaTopSpacing,
} from '@banhala/ui-kit';
import { useRoute } from '@react-navigation/native';
import { WebView } from 'shared/src/component/WebWebView';
import { isNil, isNotNil, isWeb } from 'shared/src/utils';
import { useWebViewActionFromMessage } from 'shared/src/hooks';
import { useMainTabs, useMyInfo } from 'shared/src/queries';
import { WEB_ROUTE_PATHS } from 'shared/src/webRoutePaths';
import { EMBEDDED_WEB_QUERY_KEY } from 'shared/src/constants/webviewQuery';
import {
  NotificationIcon,
  SettingIcon,
} from 'shared/src/component/HeaderIcons';
import { CartIcon } from 'shared/src/component';

/**
 * WEBVIEW 타입 하단탭 공용 화면.
 *
 * 자신의 route name(=mainTabs 의 screen_name)으로 mainTabs.item_list 에서 해당 탭을 찾아
 * default_query(url/title_bar)와 name 을 읽는다. (route params 로 넘기지 않아 URL 에
 * 파라미터가 노출되지 않는다.
 */
export const TabWebViewScreen: React.FC = () => {
  const { width: screenWidth } = useScreenSize();
  const route = useRoute();
  const { data: mainTabs } = useMainTabs();

  // 이 화면은 name={screen_name} 으로 등록되므로 route.name 이 곧 screen_name 이다.
  // (mainTabs 조회 실패 여부와 무관하게 항상 정의됨 → screenName undefined 방지)
  const screenName = route.name;
  const { data: myInfo } = useMyInfo();

  // 웹 환경에서 비로그인 상태로 마이페이지에 진입하면 iframe 대신 로그인 페이지로 이동.
  // 로그인 성공 후 redirect로 다시 마이페이지('/mypage')로 복귀.
  const shouldRedirectToLogin =
    isWeb && screenName === 'MY_PAGE' && (myInfo?.is_anonymous ?? false);
  useEffect(() => {
    if (!shouldRedirectToLogin) return;
    window.location.replace(
      `${WEB_ROUTE_PATHS.Login}?redirect=${encodeURIComponent('/mypage')}`,
    );
  }, [shouldRedirectToLogin]);

  const tab = mainTabs.item_list.find(item => item.screen_name === screenName);
  const url = tab?.default_query?.url;
  const title = tab?.name ?? '';
  // title_bar true: 웹뷰가 타이틀바를 그리지 않으므로 네이티브(StackScreen)가 헤더를 그린다.
  // false: 웹뷰 내부에서 그리므로 네이티브 헤더는 숨긴다.
  const titleBar = tab?.default_query?.title_bar ?? false;
  const webViewAction = useWebViewActionFromMessage();

  const uri = useMemo(() => {
    if (isNil(url)) return undefined;
    // return new URL(url).toString();

    const urlObj = new URL(url);

    urlObj.searchParams.set('bottom-tab', 'true');
    urlObj.searchParams.set('global_layout', 'app');
    // 웹에서는 iframe 임베드다. 콘텐츠가 프레임 구조를 추측하지 않게 명시한다.
    if (isWeb) {
      urlObj.searchParams.set(EMBEDDED_WEB_QUERY_KEY, 'true');
    }
    if (__DEV__) {
      // 로컬 웹 개발 서버를 바라보게 origin 만 교체한다(path/query 는 서버 응답 그대로 유지).
      urlObj.protocol = 'http:';
      urlObj.host = 'localhost:3000';
    }
    return urlObj.toString();
  }, [url]);
  return (
    <StackScreen
      title={title}
      hideBackButton={true}
      hideHeader={!titleBar}
      rightIcons={
        titleBar && screenName === 'MY_PAGE'
          ? [
              <SettingIcon screen={'MY_PAGE'} />,
              <NotificationIcon screen={'MY_PAGE'} />,
              <CartIcon />,
            ]
          : []
      }
    >
      {/* 네이티브 헤더가 없을 때만(=웹뷰가 타이틀바를 그림) 상단 세이프에어리어를 직접 확보 */}
      {!titleBar && <SafeAreaTopSpacing />}
      <Flex flex={1} alignItems={'center'}>
        <Flex width={screenWidth} flex={1}>
          {isNotNil(uri) && !shouldRedirectToLogin && (
            <WebView source={{ uri }} onMessage={webViewAction} />
          )}
        </Flex>
      </Flex>
    </StackScreen>
  );
};
